import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  User, Mail, Globe, Linkedin, Calendar, Award, Briefcase,
  FileText, ExternalLink, Loader2, ArrowLeft, Eye, Tag, Send, CheckCircle, UserPlus,
} from 'lucide-react';
import { getBackendHost } from '@/lib/api-client';
import MarkdownIt from 'markdown-it';
import PackageCard from '@/components/offers/PackageCard';
import type { Offer } from '@/types/offer';

const md = MarkdownIt('commonmark', { html: false, linkify: true });

/** Render markdown to HTML using the same parser as the ProseMirror editor. */
function renderMarkdown(src: string): string {
  return md.render(src);
}

interface PublicProfile {
  id: string;
  is_listed?: boolean;
  display_name: string;
  headline: string;
  bio: string;
  photo_url: string;
  industries: string;
  certifications: string[];
  years_experience: number | null;
  specializations: string;
  contact_email: string;
  booking_url: string;
  website_url: string;
  linkedin_url: string;
  stats?: { clients: number; running_contracts: number; published_articles: number };
}

interface PublicBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tags: string;
  published_at: string | null;
  view_count: number;
  ai_assisted?: boolean;
}


interface ProfileData {
  profile: PublicProfile;
  blog_posts: PublicBlogPost[];
  public_offers?: Offer[];
}

function getApiBaseUrl() {
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  const backendHost = getBackendHost(host);
  const backendPort = protocol === 'http:' && port ? ':8080' : '';
  return `${protocol}//${backendHost}${backendPort}/api/frontend`;
}

export default function PublicProfilePage() {
  const { profileId } = useParams<{ profileId: string }>();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) return;
    setLoading(true);
    fetch(`${getApiBaseUrl()}/public/advisors/${profileId}/`)
      .then((res) => {
        if (!res.ok) throw new Error('Profile not found');
        return res.json();
      })
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [profileId]);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background gap-4">
        <User className="h-16 w-16 text-muted-foreground/30" />
        <p className="text-muted-foreground">
          {error || 'Profile not found or not published.'}
        </p>
        <Link to="/public/advisors">
          <Button variant="outline" data-testid="back-to-directory">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Directory
          </Button>
        </Link>
      </div>
    );
  }

  const { profile, blog_posts } = data;
  const publicOffers = data.public_offers ?? [];

  return (
    <div className="min-h-screen w-full bg-background" data-testid="public-profile-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-amber-500/10 to-transparent">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link
            to="/public/advisors"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
            data-testid="back-to-directory"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Directory
          </Link>
          <div className="flex items-start gap-6">
            <div className="h-24 w-24 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0 border-2 border-amber-500/20">
              {profile.photo_url ? (
                <img
                  src={profile.photo_url}
                  alt={profile.display_name}
                  className="h-24 w-24 rounded-full object-cover"
                  data-testid="profile-photo"
                />
              ) : (
                <User className="h-12 w-12 text-amber-500" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight">{profile.display_name}</h1>
              </div>
              {profile.headline && (
                <p className="text-lg text-muted-foreground mt-1">{profile.headline}</p>
              )}
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                {profile.years_experience && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <Award className="h-4 w-4" />
                    {profile.years_experience} years experience
                  </span>
                )}
                {profile.industries && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <Tag className="h-4 w-4" />
                    {profile.industries}
                  </span>
                )}
              </div>
              {/* Contact links */}
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                {profile.contact_email && (
                  <a
                    href={`mailto:${profile.contact_email}`}
                    className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700"
                  >
                    <Mail className="h-4 w-4" />
                    {profile.contact_email}
                  </a>
                )}
                {profile.booking_url && (
                  <a
                    href={profile.booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700"
                  >
                    <Calendar className="h-4 w-4" />
                    Book a Meeting
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700"
                  >
                    <Globe className="h-4 w-4" />
                    Website
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700"
                  >
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-12 space-y-8">
        {/* Bio */}
        {profile.bio && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">About</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="text-sm text-muted-foreground prose prose-sm max-w-none dark:prose-invert"
                data-testid="bio-rendered"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(profile.bio) }}
              />
            </CardContent>
          </Card>
        )}

        {/* Expertise */}
        <div className="grid gap-6 md:grid-cols-2">
          {profile.specializations && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Specializations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {profile.specializations}
                </p>
              </CardContent>
            </Card>
          )}
          {profile.certifications?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Certifications</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground" data-testid="public-certifications">
                  {profile.certifications.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>


        {/* Testimonials */}
        {profile.client_testimonials && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Client Testimonials</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap italic">
                {profile.client_testimonials}
              </p>
            </CardContent>
          </Card>
        )}

        {publicOffers.length > 0 && (
          <div className="space-y-4" data-testid="public-offers">
            <h2 className="text-xl font-semibold">Ready-made offers</h2>
            <p className="text-sm text-muted-foreground">
              Pick one of these the moment you choose {profile.display_name} in the AOC; they confirm it and the contract is running.
            </p>
            {publicOffers.map((offer) => (
              <Card key={offer.id} data-testid={`public-offer-${offer.id}`}>
                <CardHeader>
                  <CardTitle className="text-base">{offer.title}</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {offer.billing_mode === 'via_aoc' ? 'Billed through BitSwan on your monthly invoice' : 'Invoiced by the advisor directly'} · prices excl. VAT
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {offer.introduction && <p className="text-sm text-muted-foreground">{offer.introduction}</p>}
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {offer.packages.map((pkg) => (
                      <PackageCard key={pkg.id} pkg={pkg} currency={offer.currency} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Blog Posts */}
        {blog_posts.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Articles</h2>
            <div className="space-y-3">
              {blog_posts.map((post) => {
                const isExpanded = expandedPostId === post.id;
                return (
                  <Card key={post.id} className="hover:border-amber-500/30 transition-colors">
                    <CardContent className="py-4">
                      <div
                        className="cursor-pointer"
                        onClick={() => setExpandedPostId(isExpanded ? null : post.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                            <FileText className="h-4 w-4 text-amber-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/public/advisor/${profileId}/blog/${post.slug || post.id}`}
                              className="font-medium text-sm hover:text-amber-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`blog-link-${post.id}`}
                            >
                              {post.title}
                              <Badge variant="outline" className="ml-2 align-middle text-[10px]" data-testid={`public-post-authorship-${post.id}`}>
                                {post.ai_assisted ? 'AI-assisted' : 'Human-written'}
                              </Badge>
                            </Link>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                              {post.published_at && (
                                <span>{new Date(post.published_at).toLocaleDateString()}</span>
                              )}
                              {post.tags && <span>{post.tags}</span>}
                              <span className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {post.view_count}
                              </span>
                            </div>
                          </div>
                        </div>
                        {post.excerpt && !isExpanded && (
                          <p className="text-xs text-muted-foreground mt-2 ml-12">
                            {post.excerpt}
                          </p>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="mt-4 ml-12 border-t pt-4">
                          <div
                            className="prose prose-sm max-w-none text-sm text-muted-foreground dark:prose-invert"
                            data-testid={`blog-content-${post.id}`}
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
        {/* Newsletter Subscribe Widget */}
        <Card className="mt-6" data-testid="subscribe-widget">
          <CardContent className="py-4">
            <SubscribeWidget profileId={profileId!} advisorName={profile.display_name} />
          </CardContent>
        </Card>

        {profile.is_listed && (
        <Card className="mt-6 border-amber-500/30 bg-amber-500/5" data-testid="profile-get-started">
          <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">Start with {profile.display_name}</p>
              <p className="text-sm text-muted-foreground">
                Create your organisation, get 30 days free with one Bailey
                server, and they pick it up from there.
              </p>
            </div>
            <Button asChild data-testid="profile-get-started-btn">
              <Link to={`/signup?advisor=${profileId}`}>
                <UserPlus className="h-4 w-4 mr-2" />
                Get started
              </Link>
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Contact Form */}
        <Card className="mt-6" data-testid="contact-advisor-form">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4 text-amber-500" />
              Contact this Advisor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ContactForm profileId={profileId!} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SubscribeWidget({ profileId, advisorName }: { profileId: string; advisorName: string }) {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async () => {
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/public/advisors/${profileId}/subscribe/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) setSubscribed(true);
    } catch {} finally {
      setSubscribing(false);
    }
  };

  if (subscribed) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600" data-testid="subscribe-success">
        <CheckCircle className="h-4 w-4" /> Subscribed! You'll receive updates from {advisorName}.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Subscribe to {advisorName}'s newsletter</p>
      <div className="flex gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="flex-1"
          data-testid="subscribe-email"
          onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
        />
        <Button
          onClick={handleSubscribe}
          disabled={subscribing || !email.trim()}
          data-testid="subscribe-btn"
        >
          {subscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
          Subscribe
        </Button>
      </div>
    </div>
  );
}

function ContactForm({ profileId }: { profileId: string }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) return;
    setSending(true);
    setError('');
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/public/advisors/${profileId}/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_name: name,
          contact_email: email,
          company_name: company,
          message,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send');
      }
      setSent(true);
    } catch (e: any) {
      setError(e.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center py-6 text-center" data-testid="contact-sent">
        <CheckCircle className="h-10 w-10 text-green-500 mb-3" />
        <p className="font-medium text-green-700">Message sent!</p>
        <p className="text-sm text-muted-foreground mt-1">The advisor will receive your message and contact details.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Your Name *</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Petr Novák" data-testid="contact-name" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Email *</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="petr@company.com" data-testid="contact-email" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Company</label>
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name (optional)" data-testid="contact-company" />
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Message</label>
        <textarea
          className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] resize-y"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell the advisor what you need help with..."
          data-testid="contact-message"
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button
        onClick={handleSubmit}
        disabled={sending || !name.trim() || !email.trim()}
        data-testid="contact-send-btn"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
        Send Message
      </Button>
    </div>
  );
}

export function PublicBlogPostPage() {
  const { profileId, slug } = useParams<{ profileId: string; slug: string }>();
  const [post, setPost] = useState<PublicBlogPost | null>(null);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/public/advisors/${profileId}/`);
        if (!res.ok) throw new Error('Not found');
        const data: ProfileData = await res.json();
        setProfile(data.profile);
        const found = data.blog_posts.find(p => p.slug === slug || p.id === slug);
        setPost(found || null);
      } catch {
        setPost(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [profileId, slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-muted-foreground">Blog post not found.</p>
        <Link to={`/public/advisor/${profileId}`}>
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background" data-testid="blog-post-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to={`/public/advisor/${profileId}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {profile?.display_name || 'Back to Profile'}
        </Link>

        <article>
          <h1 className="text-2xl font-bold mb-3" data-testid="blog-post-title">{post.title}</h1>

          {/* Author byline — engagement story: "Petr clicks Jana's name at the top" */}
          {profile && (
            <Link
              to={`/public/advisor/${profileId}`}
              className="inline-flex items-center gap-2 mb-4 p-2 -ml-2 rounded-lg hover:bg-muted/50 transition-colors"
              data-testid="blog-post-author"
            >
              <div className="h-8 w-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                <User className="h-4 w-4 text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-medium hover:text-amber-600 transition-colors">{profile.display_name}</p>
                {profile.specializations && (
                  <p className="text-xs text-muted-foreground">{profile.specializations.split(',').slice(0, 3).join(' · ')}</p>
                )}
              </div>
            </Link>
          )}

          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-8">
            {post.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(post.published_at).toLocaleDateString()}
              </span>
            )}
            {post.tags && (
              <span className="flex items-center gap-1">
                <Tag className="h-4 w-4" />
                {post.tags}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {post.view_count} views
            </span>
          </div>

          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
          />

          {/* CTA: Contact this advisor — matches engagement story */}
          {profile && (
            <div className="mt-8 p-4 rounded-lg border border-amber-500/20 bg-amber-500/5" data-testid="blog-post-cta">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                  <User className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium">{profile.display_name}</p>
                  {profile.specializations && (
                    <p className="text-xs text-muted-foreground">{profile.specializations.split(',').slice(0, 4).join(' · ')}</p>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Want to learn more? Get in touch to discuss how automation can help your business.
              </p>
              <Link to={`/public/advisor/${profileId}#contact`}>
                <Button size="sm" data-testid="blog-contact-cta">
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Contact this advisor
                </Button>
              </Link>
            </div>
          )}

          {/* Subscribe widget on blog post */}
          {profileId && profile && (
            <div className="mt-6 pt-6 border-t" data-testid="blog-subscribe-widget">
              <SubscribeWidget profileId={profileId} advisorName={profile.display_name} />
            </div>
          )}
        </article>
      </div>
    </div>
  );
}
